import React, { useState, useEffect, useRef } from "react";
import styles from "./Dropdown.module.css";
import { IoIosArrowDown } from 'react-icons/io';

const Dropdown = ({
  mode = "select", // "select" | "search"
  value,
  onChange,
  // Select-mode props
  options = [],
  placeholder = "Select...",
  // Search-mode props
  inputValue = "",
  onInputChange = () => {},
  suggestions = [],
  loading = false,
  // Parent controls visibility for search
  showSuggestions = false,
  setShowSuggestions = () => {},
  // Parent controls keyboard / active index
  activeIndex = -1,
  setActiveIndex = () => {},
  onSelect = (item) => {},
  // Handler for Enter when no active selection
  onEnterWithoutSelection = () => {},
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Select-mode open state
  const [open, setOpen] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
        setTimeout(() => setShowSuggestions(false), 0); // preserve slight delay
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setShowSuggestions]);

  // Focus input when showing search suggestions
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSuggestions]);

  const isSelect = mode === "select";
  const isSearch = mode === "search";

  return (
    <div className={styles.dropdownContainer} ref={containerRef}>
      {/* Select Mode */}
      {isSelect && (
        <>
          <div
            className={styles.dropdownButton}
            onClick={() => setOpen((o) => !o)}
          >
            {value || placeholder}
            <IoIosArrowDown className={styles.dropdownIcon}/>
          </div>

          {open && (
            <div className={styles.dropdownList}>
              {options.map((opt, idx) => (
                <div
                  key={idx}
                  className={styles.dropdownItem}
                  onMouseDown={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Search Mode */}
      {isSearch && (
        <div className={styles.searchWrapper}>
          <input
            ref={inputRef}
            className={styles.dropdownSearch}
            type="text"
            placeholder="Search players..."
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onFocus={() => setShowSuggestions(suggestions.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
            onKeyDown={(e) => {
              const len = suggestions.length;

              if ((e.key === "ArrowDown" || e.key === "ArrowUp") && len > 0) {
                setShowSuggestions(true);
              }

              if (e.key === "ArrowDown" && len > 0) {
                e.preventDefault();
                setActiveIndex((i) => (i + 1) % len);
                return;
              }

              if (e.key === "ArrowUp" && len > 0) {
                e.preventDefault();
                setActiveIndex((i) => (i - 1 + len) % len);
                return;
              }

              if (e.key === "Enter") {
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < len) {
                  onSelect(suggestions[activeIndex]);
                } else {
                  onEnterWithoutSelection();
                }
              }
            }}
          />

          {showSuggestions && (
            <div className={styles.dropdownList}>
              {loading && (
                <div className={styles.dropdownItemMuted}>Loading players...</div>
              )}

              {!loading &&
                suggestions.map((p, idx) => (
                  <div
                    key={p.player_id}
                    ref={
                      idx === activeIndex
                        ? (el) => {
                            if (el && typeof el.scrollIntoView === "function") {
                              el.scrollIntoView({ block: "nearest" });
                            }
                          }
                        : null
                    }
                    className={`${styles.dropdownItem} ${
                      idx === activeIndex ? styles.active : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(p);
                    }}
                  >
                    {p.first_name} {p.last_name}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
